import { useState, useEffect } from 'react'
import { X, Loader2 } from 'lucide-react'
import { adminApi } from '@/services/api/admin'
import { PriorityRequestCard } from '@/components/teacher-request/PriorityRequestCard'
import type { PriorityRequestListItem } from '@/services/api/teacherPriority'
import { getExpiryText } from '@/utils/requestExpiry'
import { useToast } from '@/components/ui/ToastProvider'
import { useAdminDashboardConfig } from '@/hooks/useAdminDashboardConfig'

interface AdminRequestCardModalProps {
  requestId: string
  onClose: () => void
  onChanged: () => void
}

export function AdminRequestCardModal({ requestId, onClose, onChanged }: AdminRequestCardModalProps) {
  const [request, setRequest] = useState<PriorityRequestListItem | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isCancelling, setIsCancelling] = useState<string | null>(null)
  const { showToast } = useToast()
  const { data: dashboardConfig } = useAdminDashboardConfig()

  const fetchCard = () => {
    setLoading(true)
    setError(null)
    adminApi.getSubstitutionCard(requestId)
      .then(setRequest)
      .catch(() => setError('Karte konnte nicht geladen werden'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchCard()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId])

  // Escape zum Schliessen
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const handleCancel = async (id: string, _status: string) => {
    if (!window.confirm('Anfrage wirklich stornieren?')) return
    setIsCancelling(id)
    try {
      await adminApi.cancelSubstitution(id)
      showToast({ message: 'Anfrage storniert', type: 'success', duration: 3000 })
      onChanged()
      onClose()
    } catch {
      showToast({ message: 'Stornierung fehlgeschlagen', type: 'error', duration: 3000 })
    } finally {
      setIsCancelling(null)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-gray-50 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-white rounded-t-xl sticky top-0 z-10">
          <h3 className="font-semibold text-sm">Anfrage-Karte (Lehrer-Sicht)</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchCard}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Aktualisieren"
            >
              <Loader2 className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Schliessen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="animate-pulse space-y-3">
              <div className="h-6 bg-gray-200 rounded w-1/3" />
              <div className="h-4 bg-gray-200 rounded w-2/3" />
              <div className="h-24 bg-gray-200 rounded" />
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-600 text-sm">{error}</div>
          ) : request ? (
            <PriorityRequestCard
              request={request}
              expiryInfo={getExpiryText(request.expires_at)}
              isCancelling={isCancelling}
              isPrepResolving={null}
              onCancel={handleCancel}
              onMarkPrepFulfilled={() => {}}
              onReRequest={() => {
                showToast({ message: 'Nur die anfragende Lehrperson kann erneut anfragen.', type: 'info', duration: 4000 })
              }}
              cascadePreviewFetcher={adminApi.getCascadePreview}
              autoRefreshPreview={dashboardConfig?.admin_card_auto_refresh_preview ?? false}
              suppressInternalFetches
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}
